import { Routes, Route, Navigate } from 'react-router-dom'
import { PublicForm } from '@/features/public/PublicForm'
import { AdminDashboard } from '@/features/admin/AdminDashboard'
import { LoginPage } from '@/features/auth/LoginPage'
import { Dashboard } from '@/features/dashboard/Dashboard'
import { FAQPage } from '@/features/faq/FAQPage'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { AdminAuthProvider } from '@/contexts/AdminAuthContext'
import { ToastProvider } from '@/components/ui/toast-notification'

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Načítání...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Public route component (redirects authenticated users)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Načítání...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <div className="min-h-screen bg-background">
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        
        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/form" 
          element={
            <ProtectedRoute>
              <AdminAuthProvider>
                <PublicForm />
              </AdminAuthProvider>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/faq" 
          element={
            <ProtectedRoute>
              <FAQPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin route (separate authentication) */}
        <Route 
          path="/admin" 
          element={
            <AdminAuthProvider>
              <AdminDashboard />
            </AdminAuthProvider>
          } 
        />
        
        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AuthProvider>
  )
}

export default App 